import React, { useState, useEffect, useCallback } from 'react';
import { 
    Loader2, Printer, RefreshCcw, ArrowLeft, 
    FileText, Calendar, TrendingUp, Bed, ShoppingCart, Layers
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import API, { socket } from '../../services/api';

const RapportCA = () => {
    const navigate = useNavigate();
    
    // --- ÉTATS ---
    const [loading, setLoading] = useState(false);
    const [caData, setCaData] = useState(null);
    
    // Filtres
    const [periode, setPeriode] = useState('jour');
    const [dateDebut, setDateDebut] = useState('');
    const [dateFin, setDateFin] = useState('');

    // Récupération des données du Chiffre d'Affaires
    const fetchCA = useCallback(async () => {
        setLoading(true);
        try {
            const params = { periode };
            if (dateDebut) params.date_debut = dateDebut;
            if (dateFin) params.date_fin = dateFin;

            const res = await API.get('/manager/rapports/chiffre-affaires', { params });
            if (res.data.success) {
                setCaData(res.data.data);
            }
        } catch (err) { 
            console.error("Erreur lors de la récupération du Chiffre d'Affaires:", err); 
        } finally { 
            setLoading(false); 
        }
    }, [periode, dateDebut, dateFin]);

    useEffect(() => {
        fetchCA();

        // 📡 CONFIGURATION TEMPS RÉEL (Socket)
        if (socket) {
            const handleRefresh = () => {
                fetchCA();
            };

            socket.on('REFRESH_UI', handleRefresh);
            socket.on('DATA_EVENT', handleRefresh);

            return () => {
                socket.off('REFRESH_UI', handleRefresh);
                socket.off('DATA_EVENT', handleRefresh);
            };
        }
    }, [fetchCA]);

    const formatCur = (v) => {
        if (v === undefined || v === null || isNaN(v) || v === 0) return "-";
        return Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 0 }) + ' FCFA';
    };

    // Vérifications strictes pour masquer dynamiquement ce qui est vide ou nul
    const caHebergement = Number(caData?.ca_hebergement || 0);
    const caRestauration = Number(caData?.ca_restauration || 0);
    const caExtras = Number(caData?.ca_extras || 0);
    const caHotelier = Number(caData?.ca_hotelier || 0);
    const caVentes = Number(caData?.ca_ventes || 0);

    const hasHotelData = caHotelier > 0 || caHebergement > 0 || caRestauration > 0 || caExtras > 0;
    const hasCommercialData = caVentes > 0;

    return (
        <div style={s.layout}>
            <Sidebar />
            <main style={s.main}>
                <header style={s.header}>
                    <div style={s.headerLeft}>
                        <button onClick={() => navigate(-1)} style={s.btnBack}><ArrowLeft size={20} /></button>
                        <div style={s.titleContainer}>
                            <h2 style={s.title}>Rapport du Chiffre d'Affaires (C.A.)</h2>
                            <div style={s.subtitleContainer}>
                                <span style={s.statusDot}></span>
                                <span style={s.subtitle}>Hôtel Chaine B • Performances Commerciales & Hôtelières</span>
                            </div>
                        </div>
                    </div>
                    <div style={s.headerRight}>
                        <button style={s.btnAction} onClick={fetchCA}>
                            {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCcw size={16} />} ACTUALISER
                        </button>
                        <button style={s.btnIconOnly} onClick={() => window.print()}><Printer size={20} /></button>
                    </div>
                </header>

                {/* BARRE DE FILTRES */}
                <section style={s.filterBar}>
                    <div style={s.filterGrid}>
                        <div style={s.fGroup}>
                            <label style={s.fLabel}>Période</label>
                            <select 
                                style={s.fSelect} 
                                value={periode} 
                                onChange={e => { setPeriode(e.target.value); setDateDebut(''); setDateFin(''); }}
                            >
                                <option value="jour">Aujourd'hui (Journalier)</option>
                                <option value="semaine">Cette Semaine (Hebdomadaire)</option>
                                <option value="mois">Ce Mois (Mensuel)</option>
                                <option value="annee">Cette Année (Annuel)</option>
                            </select>
                        </div>
                        <div style={s.fGroup}>
                            <label style={s.fLabel}>Date Début (Optionnel)</label>
                            <input 
                                type="date" 
                                style={s.fInput} 
                                value={dateDebut} 
                                onChange={e => setDateDebut(e.target.value)} 
                            />
                        </div>
                        <div style={s.fGroup}>
                            <label style={s.fLabel}>Date Fin (Optionnel)</label>
                            <input 
                                type="date" 
                                style={s.fInput} 
                                value={dateFin} 
                                onChange={e => setDateFin(e.target.value)} 
                            />
                        </div>
                    </div>
                </section>

                <div style={s.tableArea}>
                    <div style={s.tableWrapper}>
                        {loading && !caData ? (
                            <div style={s.load}><Loader2 className="animate-spin" size={50} color="#008000"/></div>
                        ) : caData ? (
                            <table style={s.table}>
                                <thead style={s.thead}>
                                    <tr style={s.trHMain}>
                                        <th style={s.th}><TrendingUp size={16} style={{display:'inline', marginRight:6}}/> SECTION / ACTIVITÉ</th>
                                        <th style={s.thR}>CHIFFRE D'AFFAIRES</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* --- ACTIVITÉ HÔTELIÈRE (Masquée si aucune donnée d'hébergement/extras) --- */}
                                    {hasHotelData && (
                                        <>
                                            <tr style={s.trSecH}>
                                                <td colSpan={2} style={s.tdSecT}><Bed size={15} style={{marginRight:8, verticalAlign:'middle'}}/> PÉRIMÈTRE HÔTELIER</td>
                                            </tr>
                                            {caHebergement > 0 && (
                                                <tr style={s.trMain}>
                                                    <td style={s.tD}>• C.A. Hébergement (Chambres & Nuitées)</td>
                                                    <td style={s.tD_Amt}>{formatCur(caData.ca_hebergement)}</td>
                                                </tr>
                                            )}
                                            {caRestauration > 0 && (
                                                <tr style={s.trMain}>
                                                    <td style={s.tD}>• C.A. Restauration (Liée à l'hôtel)</td>
                                                    <td style={s.tD_Amt}>{formatCur(caData.ca_restauration)}</td>
                                                </tr>
                                            )}
                                            {caExtras > 0 && (
                                                <tr style={s.trMain}>
                                                    <td style={s.tD}>• C.A. Extras & Services hôteliers</td>
                                                    <td style={s.tD_Amt}>{formatCur(caData.ca_extras)}</td>
                                                </tr>
                                            )}
                                            <tr style={s.trTotSec}>
                                                <td style={s.tdTotSecL}>SOUS-TOTAL C.A. HÔTELIER</td>
                                                <td style={s.tdTotSecV}>{formatCur(caData.ca_hotelier)}</td>
                                            </tr>
                                        </>
                                    )}

                                    {/* --- ACTIVITÉ COMMERCIALE / POS (Masquée si aucune vente Gestock) --- */}
                                    {hasCommercialData && (
                                        <>
                                            <tr style={s.trSecH}>
                                                <td colSpan={2} style={s.tdSecT}><ShoppingCart size={15} style={{marginRight:8, verticalAlign:'middle'}}/> PÉRIMÈTRE COMMERCIAL / POS (GESTOK)</td>
                                            </tr>
                                            <tr style={s.trMain}>
                                                <td style={s.tD}>• C.A. Ventes Commerciales Générales</td>
                                                <td style={s.tD_Amt}>{formatCur(caData.ca_ventes)}</td>
                                            </tr>
                                            <tr style={s.trTotSec}>
                                                <td style={s.tdTotSecL}>SOUS-TOTAL C.A. COMMERCIAL</td>
                                                <td style={s.tdTotSecV}>{formatCur(caData.ca_ventes)}</td>
                                            </tr>
                                        </>
                                    )}

                                    {/* --- C.A. GLOBAL FINAL --- */}
                                    <tr style={s.trGlobal}>
                                        <td style={s.tdGlobalL}>CHIFFRE D'AFFAIRES GLOBAL DE L'ENTREPRISE</td>
                                        <td style={s.tdGlobalV}>{formatCur(caData.chiffre_affaires_total)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        ) : (
                            <div style={s.noData}><FileText size={40}/><p>Aucune donnée disponible pour cette période</p></div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

const s = {
    layout: { display: 'flex', height: '100vh', background: '#f8fafc' },
    main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    header: { background: '#008000', padding: '15px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white' },
    headerLeft: { display: 'flex', alignItems: 'center', gap: '20px' },
    titleContainer: { display: 'flex', flexDirection: 'column' },
    title: { margin: 0, fontSize: '18px', fontWeight: '900' },
    subtitleContainer: { display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' },
    statusDot: { width: '8px', height: '8px', background: '#4ade80', borderRadius: '50%' },
    subtitle: { fontSize: '11px', color: '#e8f5e9' },
    headerRight: { display: 'flex', alignItems: 'center', gap: '12px' },
    btnBack: { background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '10px', color: 'white', padding: '8px', cursor: 'pointer' },
    btnAction: { background: 'white', color: '#008000', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' },
    btnIconOnly: { background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', padding: '10px', borderRadius: '8px', cursor: 'pointer' },
    filterBar: { background: 'white', padding: '20px 40px', borderBottom: '1px solid #e2e8f0' },
    filterGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' },
    fGroup: { display: 'flex', flexDirection: 'column', gap: '4px', position: 'relative' },
    fLabel: { fontSize: '10px', fontWeight: 'bold', color: '#008000' },
    fInput: { padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px' },
    fSelect: { padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px', background: 'white' },
    tableArea: { flex: 1, padding: '20px', overflow: 'hidden' },
    tableWrapper: { height: '100%', background: 'white', borderRadius: '10px', overflow: 'auto', border: '1px solid #e2e8f0' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
    thead: { position: 'sticky', top: 0, background: 'white', zIndex: 10 },
    trHMain: { borderBottom: '2px solid #008000' },
    th: { padding: '12px 20px', textAlign: 'left', color: '#008000', fontWeight: 'bold' },
    thR: { padding: '12px 20px', textAlign: 'right', color: '#008000', fontWeight: 'bold' },
    trSecH: { background: '#f1f5f9', borderTop: '1px solid #cbd5e1', borderBottom: '1px solid #cbd5e1' },
    tdSecT: { padding: '10px 20px', fontWeight: 'bold', color: '#008000' },
    trMain: { borderBottom: '1px solid #f1f5f9' },
    tD: { padding: '12px 20px', fontWeight: '500', paddingLeft: '35px' },
    tD_Amt: { textAlign: 'right', padding: '12px 20px', fontWeight: '600', color: '#1e293b' },
    trTotSec: { background: '#f0fff4', borderTop: '2px solid #008000', fontWeight: 'bold' },
    tdTotSecL: { padding: '12px 20px', color: '#008000' },
    tdTotSecV: { textAlign: 'right', padding: '12px 20px', color: '#008000' },
    trGlobal: { background: '#004d40', color: 'white', fontWeight: '900', borderTop: '4px solid #000' },
    tdGlobalL: { padding: '15px 20px', fontSize: '13px' },
    tdGlobalV: { textAlign: 'right', padding: '15px 20px', fontSize: '15px', color: '#a7ffeb' },
    load: { display: 'flex', justifyContent: 'center', padding: '50px' },
    noData: { textAlign: 'center', padding: '50px', color: '#94a3b8' }
};

export default RapportCA;